import { useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Brain, Zap, Shield, TrendingUp, Building2, DollarSign, Briefcase,
  PiggyBank, AlertTriangle, CheckCircle2, Clock, ArrowRight,
  Loader2, ChevronDown, ChevronRight, Sparkles, Target, BarChart3
} from "lucide-react";

const formatCurrency = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v || 0);

const CATEGORY_CONFIG = {
  retirement: { icon: PiggyBank, color: "text-pink-400", bg: "bg-pink-500/10", border: "border-pink-500/20" },
  investment: { icon: TrendingUp, color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/20" },
  real_estate: { icon: Building2, color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/20" },
  deduction: { icon: DollarSign, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
  credit: { icon: Shield, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
  income: { icon: BarChart3, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
  business: { icon: Briefcase, color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/20" },
};

const PRIORITY_STYLES = {
  high: "bg-red-500/20 text-red-400 border-red-500/30",
  medium: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  low: "bg-blue-500/20 text-blue-400 border-blue-500/30",
};

function StrategyCard({ strategy, index }) {
  const [expanded, setExpanded] = useState(false);
  const config = CATEGORY_CONFIG[strategy.category] || CATEGORY_CONFIG.deduction;
  const Icon = config.icon;

  return (
    <Card
      data-testid={`strategy-card-${index}`}
      className={`bg-[#0A0A0A] border-white/10 rounded-none overflow-hidden hover:border-white/20 transition-colors`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-5 flex items-start gap-4"
      >
        <div className={`w-10 h-10 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
          <Icon className={`w-5 h-5 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-sm font-semibold text-white">{strategy.title}</h3>
            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${PRIORITY_STYLES[strategy.priority] || PRIORITY_STYLES.medium}`}>
              {strategy.priority}
            </span>
            {strategy.complexity && (
              <span className="text-[10px] text-zinc-500 bg-white/5 px-1.5 py-0.5 rounded">{strategy.complexity}</span>
            )}
          </div>
          <p className="text-xs text-zinc-400 line-clamp-2">{strategy.description}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-lg font-bold text-emerald-400 font-mono">
            {strategy.estimated_savings > 0 ? `+${formatCurrency(strategy.estimated_savings)}` : "See details"}
          </p>
          <p className="text-[10px] text-zinc-500">est. savings</p>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-zinc-500 mt-1" /> : <ChevronRight className="w-4 h-4 text-zinc-500 mt-1" />}
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-white/5 pt-4 space-y-4">
          {strategy.action_steps && strategy.action_steps.length > 0 && (
            <div>
              <h4 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Action Steps</h4>
              <div className="space-y-2">
                {strategy.action_steps.map((step, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[10px] font-bold text-primary">{i + 1}</span>
                    </div>
                    <p className="text-sm text-zinc-300">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-6 text-xs text-zinc-500">
            {strategy.deadline && (
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>{strategy.deadline}</span>
              </div>
            )}
            {strategy.risk_level && (
              <div className="flex items-center gap-1">
                <Shield className="w-3 h-3" />
                <span>Risk: {strategy.risk_level}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function AITaxAdvisor() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [filingStatus, setFilingStatus] = useState("single");
  const [taxYear, setTaxYear] = useState("2026");

  const runAnalysis = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post("/ai/tax-strategy", {
        tax_year: parseInt(taxYear),
        filing_status: filingStatus,
      });
      setResult(res.data);
      toast.success("Tax strategy analysis complete!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const strategies = result?.strategies || [];
  const highPriority = strategies.filter(s => s.priority === "high");
  const medPriority = strategies.filter(s => s.priority === "medium");
  const lowPriority = strategies.filter(s => s.priority === "low");

  return (
    <Layout title="AI Tax Strategy Advisor">
      <div data-testid="ai-tax-advisor-page" className="space-y-6">
        {/* Run Analysis Panel */}
        <Card className="bg-[#0A0A0A] border-primary/30 rounded-none">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">AI Tax Strategy Scan</h2>
                    <p className="text-xs text-zinc-500">Analyzes your W-2s, 1099s, investments, properties, and expenses to find savings</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Select value={taxYear} onValueChange={setTaxYear}>
                  <SelectTrigger data-testid="tax-year-select" className="w-28 bg-black/50 border-white/10 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="2026">2026</SelectItem>
                    <SelectItem value="2025">2025</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filingStatus} onValueChange={setFilingStatus}>
                  <SelectTrigger data-testid="filing-status-select" className="w-44 bg-black/50 border-white/10 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="married_jointly">Married (Joint)</SelectItem>
                    <SelectItem value="married_separately">Married (Sep)</SelectItem>
                    <SelectItem value="head_of_household">Head of Household</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  data-testid="run-analysis-btn"
                  onClick={runAnalysis}
                  disabled={loading}
                  className="bg-primary text-black font-bold rounded-sm hover:bg-primary/90 px-6"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Run Tax Scan</>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
              <Brain className="w-8 h-8 text-primary" />
            </div>
            <div className="text-center">
              <p className="text-white font-medium">Analyzing your financial data...</p>
              <p className="text-xs text-zinc-500 mt-1">Reviewing tax documents, investments, properties, and expenses</p>
            </div>
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <>
            {/* Summary Banner */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card data-testid="total-savings-card" className="bg-[#0A0A0A] border-emerald-500/20 rounded-none">
                <CardContent className="p-5">
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Total Estimated Savings</p>
                  <p className="text-3xl font-bold text-emerald-400 font-mono mt-2">
                    {formatCurrency(result.total_estimated_savings)}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">across {strategies.length} strategies</p>
                </CardContent>
              </Card>

              <Card data-testid="efficiency-score-card" className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardContent className="p-5">
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Tax Efficiency Score</p>
                  <div className="flex items-end gap-2 mt-2">
                    <p className={`text-3xl font-bold font-mono ${
                      (result.tax_efficiency_score || 0) >= 80 ? "text-emerald-400" :
                      (result.tax_efficiency_score || 0) >= 60 ? "text-amber-400" : "text-red-400"
                    }`}>
                      {result.tax_efficiency_score || 0}
                    </p>
                    <p className="text-sm text-zinc-500 mb-1">/ 100</p>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-2 mt-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-1000 ${
                        (result.tax_efficiency_score || 0) >= 80 ? "bg-emerald-500" :
                        (result.tax_efficiency_score || 0) >= 60 ? "bg-amber-500" : "bg-red-500"
                      }`}
                      style={{ width: `${result.tax_efficiency_score || 0}%` }}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardContent className="p-5">
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Data Snapshot</p>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <p className="text-[10px] text-zinc-600">Gross Income</p>
                      <p className="text-sm font-mono text-white">{formatCurrency(result.data_snapshot?.gross_income)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-zinc-600">Investments</p>
                      <p className="text-sm font-mono text-white">{formatCurrency(result.data_snapshot?.investment_portfolio_value)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-zinc-600">Properties</p>
                      <p className="text-sm font-mono text-white">{formatCurrency(result.data_snapshot?.total_property_value)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-zinc-600">401(k)</p>
                      <p className="text-sm font-mono text-white">{formatCurrency(result.data_snapshot?.current_401k_contributions)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* AI Summary */}
            {result.summary && (
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <Brain className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">AI Summary</p>
                      <p className="text-sm text-zinc-300 leading-relaxed">{result.summary}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Warnings */}
            {result.warnings && result.warnings.length > 0 && (
              <Card className="bg-[#0A0A0A] border-red-500/20 rounded-none">
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-red-400 flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4" />
                    Urgent Warnings
                  </h3>
                  <div className="space-y-2">
                    {result.warnings.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                        <AlertTriangle className="w-3 h-3 text-red-400 mt-1 flex-shrink-0" />
                        <span>{w}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Wins */}
            {result.quick_wins && result.quick_wins.length > 0 && (
              <Card className="bg-[#0A0A0A] border-emerald-500/20 rounded-none">
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-emerald-400 flex items-center gap-2 mb-3">
                    <Zap className="w-4 h-4" />
                    Quick Wins (Under 1 Hour)
                  </h3>
                  <div className="space-y-2">
                    {result.quick_wins.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                        <CheckCircle2 className="w-3 h-3 text-emerald-400 mt-1 flex-shrink-0" />
                        <span>{w}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Strategies by Priority */}
            {highPriority.length > 0 && (
              <div>
                <h3 data-testid="high-priority-header" className="text-sm font-mono uppercase tracking-widest text-red-400 mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  High Priority ({highPriority.length})
                </h3>
                <div className="space-y-3">
                  {highPriority.map((s, i) => <StrategyCard key={i} strategy={s} index={i} />)}
                </div>
              </div>
            )}

            {medPriority.length > 0 && (
              <div>
                <h3 className="text-sm font-mono uppercase tracking-widest text-amber-400 mb-3 flex items-center gap-2">
                  <ArrowRight className="w-4 h-4" />
                  Medium Priority ({medPriority.length})
                </h3>
                <div className="space-y-3">
                  {medPriority.map((s, i) => <StrategyCard key={i} strategy={s} index={highPriority.length + i} />)}
                </div>
              </div>
            )}

            {lowPriority.length > 0 && (
              <div>
                <h3 className="text-sm font-mono uppercase tracking-widest text-blue-400 mb-3 flex items-center gap-2">
                  <ArrowRight className="w-4 h-4" />
                  Low Priority ({lowPriority.length})
                </h3>
                <div className="space-y-3">
                  {lowPriority.map((s, i) => <StrategyCard key={i} strategy={s} index={highPriority.length + medPriority.length + i} />)}
                </div>
              </div>
            )}

            {/* No strategies fallback */}
            {strategies.length === 0 && result.summary && (
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardContent className="p-6 text-center">
                  <p className="text-sm text-zinc-400">
                    Add more financial data (W-2s, 1099s, investments, properties) to get detailed strategy recommendations.
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Empty State */}
        {!result && !loading && (
          <div className="flex flex-col items-center justify-center py-16 space-y-4">
            <div className="w-20 h-20 rounded-full bg-white/[0.02] border border-white/5 flex items-center justify-center">
              <Brain className="w-10 h-10 text-zinc-600" />
            </div>
            <div className="text-center max-w-md">
              <h3 className="text-lg font-semibold text-white mb-2">Ready to Optimize Your Taxes</h3>
              <p className="text-sm text-zinc-500">
                Click "Run Tax Scan" to analyze your complete financial profile. The AI will review your W-2s, 1099s, investments, properties, and expenses to find personalized tax-saving opportunities.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {["Missed Deductions", "Roth Conversions", "Tax-Loss Harvesting", "Cost Segregation", "Credit Optimization"].map(tag => (
                  <span key={tag} className="text-[10px] text-zinc-500 bg-white/5 px-2 py-1 rounded">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
